import { useEffect } from 'react';
import './StoneEdgeAnimation.css';

/**
 * PROFESSIONAL STONE EDGE ANIMATION
 * Rock-type physical move - sharp stones stab up from below
 * 
 * Key Features:
 * - Rock-type physical attack (100 base power, 80% accuracy)
 * - High critical hit ratio
 * - Sharp stone pillars erupting from the ground beneath opponent
 * - Professional gaming quality with jagged rock effects
 */
export default function StoneEdgeAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const elements = [];

    // PHASE 1: Seismic Charge (0-400ms)
    playerEl.classList.add('stone-edge-user-charge');

    const seismicPulse = document.createElement('div');
    seismicPulse.className = 'stone-edge-seismic-pulse';
    seismicPulse.style.left = `${playerX - 60}px`;
    seismicPulse.style.top = `${playerY + 10}px`;
    container.appendChild(seismicPulse);
    elements.push(seismicPulse);

    // PHASE 2: Ground Cracks Toward Opponent (400-800ms)
    const phase2Timer = setTimeout(() => {
      const dx = opponentX - playerX;
      const dy = opponentY - playerY;
      for (let i = 0; i < 6; i++) {
        const crackTimer = setTimeout(() => {
          const crack = document.createElement('div');
          crack.className = 'stone-edge-ground-crack';
          const progress = (i + 1) / 7;
          crack.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 20 - 15}px`;
          crack.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 10 - 3}px`;
          crack.style.width = `${25 + Math.random() * 15}px`;
          container.appendChild(crack);
          elements.push(crack);
        }, i * 50);
        elements.push({ timer: crackTimer, type: 'timer' });
      }
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Stone Pillars Erupting (800-1400ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('stone-edge-opponent-hit');

      // Sharp stone pillars
      const pillarPositions = [-40, -20, 0, 20, 40];
      for (let i = 0; i < pillarPositions.length; i++) {
        const pillarTimer = setTimeout(() => {
          const pillar = document.createElement('div');
          pillar.className = 'stone-edge-pillar';
          pillar.style.left = `${opponentX + pillarPositions[i] - 8}px`;
          pillar.style.top = `${opponentY + 15}px`;
          pillar.style.height = `${60 + Math.random() * 30}px`;
          container.appendChild(pillar);
          elements.push(pillar);
        }, i * 50);
        elements.push({ timer: pillarTimer, type: 'timer' });
      }

      // Rock debris flying
      for (let i = 0; i < 16; i++) {
        const debrisAngle = (i / 16) * 360;
        const debrisDist = 50 + Math.random() * 40;
        const debris = document.createElement('div');
        debris.className = 'stone-edge-debris';
        debris.style.left = `${opponentX - 4}px`;
        debris.style.top = `${opponentY - 4}px`;
        debris.style.setProperty('--scatter-x', `${Math.cos(debrisAngle * Math.PI / 180) * debrisDist}px`);
        debris.style.setProperty('--scatter-y', `${Math.sin(debrisAngle * Math.PI / 180) * debrisDist - 20}px`);
        debris.style.animationDelay = `${i * 15}ms`;
        container.appendChild(debris);
        elements.push(debris);
      }

      // Impact flash
      const impact = document.createElement('div');
      impact.className = 'stone-edge-impact';
      impact.style.left = `${opponentX - 90}px`;
      impact.style.top = `${opponentY - 90}px`;
      container.appendChild(impact);
      elements.push(impact);

      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'stone-edge-shockwave';
          sw.style.left = `${opponentX - 70}px`;
          sw.style.top = `${opponentY - 70}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }
    }, 800);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // CLEANUP (1700ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('stone-edge-user-charge');
      opponentEl.classList.remove('stone-edge-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1700);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('stone-edge-user-charge');
      opponentEl.classList.remove('stone-edge-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

StoneEdgeAnimation.moveType = 'rock';
